import type { FC } from "react";

import { resolveModelIconPair } from "@arti/utilities";

import { ModelProviderIcon } from "../common/ModelProviderIcon";
import { getFamilyAccent, resolveModel } from "./modelRegistry";

interface ModelChipProps {
  modelRef: string;
}

/**
 * Per-model bordered pill — model-vendor icon (+ serving-provider corner
 * badge) + UPPERCASE family/version label.
 *
 * Silhouette inherits the retro-terminal dialect (font-mono text-[9px]
 * uppercase tracking-widest, border-only). Per-family palette comes from the
 * registry so adding a new model is one entry there. The serving route
 * (OpenRouter / AWS Bedrock incl. mantle transport) is carried by the icon
 * badge and the hover title rather than border styling.
 *
 * The chip is an atomic token: labels contain spaces ("GLM 5.2"), so without
 * `whitespace-nowrap` it breaks across lines in narrow containers like the
 * ~240px channel rail and the bordered pill turns into a two-line box. It
 * truncates instead when space runs out, with the full model ref kept in the
 * title.
 */
export const ModelChip: FC<ModelChipProps> = ({ modelRef }) => {
  const { family, isBedrock, label } = resolveModel(modelRef);
  const accent = getFamilyAccent(family);
  const textClass = isBedrock ? accent.textBedrock : accent.text;
  const { transportLabel } = resolveModelIconPair(modelRef);

  return (
    <span
      className={`inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap border ${accent.border} ${textClass} px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest`}
      title={transportLabel ? `${modelRef} — ${transportLabel}` : modelRef}
    >
      <ModelProviderIcon
        modelRef={modelRef}
        className="h-2.5 w-2.5 shrink-0"
        badgeClassName="h-1.5 w-1.5"
      />
      <span className="truncate">{label}</span>
    </span>
  );
};
